import { useState } from 'react'
import { VpButton } from '@vtmn-play/react'
import { useTranslation } from 'react-i18next'
import { LoaderFunctionArgs, useNavigate } from 'react-router-dom'
import { userPreferencesPort } from '@/infra/config/beans'
import { useUserPreferences } from '@/src/hooks/use-user-preference-port/useUserPreferencePort'
import { usePageHeaders } from '@/src/hooks/use-page-headers/usePageHeaders'
import SearchComponent from '@/src/components/search-component/SearchComponent'
import { SearchItem } from '@/src/components/search-item/SearchItem'
import { VpDivider } from '@vtmn-play/react/VpDivider'
import { runUserPreferencesGuard } from '@/src/router/guards/runUserPreferenceGuard'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getStores } from '@/infra/api/generated/stores/stores'
import { StoreDto } from '@/infra/api/generated/model'

const { findStores } = getStores()

export const Loader = ({ request }: LoaderFunctionArgs) => {
  const currentPath = new URL(request.url).pathname
  return runUserPreferencesGuard(userPreferencesPort, currentPath)
}

const SelectStorePage = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [query, setQuery] = useState('')
  const { data, error } = useQuery({
    queryKey: ['stores'],
    queryFn: findStores,
  })
  const { t } = useTranslation('common')
  usePageHeaders(t('pages.select-store.title'))
  const { store } = useUserPreferences(userPreferencesPort)
  const [selectedStore, setSelectedStore] = useState<StoreDto | undefined>(store)

  const stores = data ?? []
  const filtered = stores.filter(
    (store) =>
      store.id.toString().includes(query) || store.name.toLowerCase().includes(query.toLowerCase())
  )
  const hasQuery = query.trim().length > 0

  const handleSelect = async (store: StoreDto) => {
    setSelectedStore(store)
    await queryClient.invalidateQueries({
      predicate: (query) => ['diagnosis', 'actions', 'teams'].includes(query.queryKey[0] as string),
    })
    userPreferencesPort.upsertStore(store)
  }

  return (
    <div className="h-full flex flex-col bg-white">
      <p className="text-l text-start uppercase font-semibold px-5">
        {t('pages.select-store.where-do-you-work')}
      </p>
      <SearchComponent
        query={query}
        setQuery={setQuery}
        searchLabel="pages.select-store.search"
        error={error}
        startSearchLabel={t('components.search.start-label', {
          labelContext: t('global.store', { count: 2 }),
        })}
      >
        {hasQuery &&
          filtered.map((store) => (
            <div key={store.id}>
              <SearchItem
                onClick={() => handleSelect(store)}
                selected={selectedStore?.id === store.id}
                label={`${store.id} - ${store.name}`}
              />
              <VpDivider />
            </div>
          ))}
      </SearchComponent>

      <div className="sticky bottom-0 px-5 pb-3">
        <VpButton
          data-testid="continue-button"
          className="w-full"
          disabled={!store?.id}
          shape="squared"
          onClick={() => navigate('/select-team', { viewTransition: true })}
        >
          {t('global.continue')}
        </VpButton>
      </div>
    </div>
  )
}

export default SelectStorePage
